import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";
import { Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AccessDenied from "components/AccessDenied";
import AlertDialog from "components/AlertDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { useMemo, useState } from "react";
import templateCategoryService from "services/sheet-template-categories-service";
import useOpen from "utils/useOpen";
import TemplateCategoriesDialog from "./TemplateCategoriesDialog";

export default function AllTemplateCategories({ permissions }) {
  const { label } = useLanguage();
  const open = useOpen({ manageDialog: false, alert: false });
  const can = usePermissionMonitor();

  const [templateCategories, setTemplateCategories] = useState(null);
  const [lastFilter, setLastFilter] = useState(null);

  async function handleAddTemplateCategory(newCategory) {
    const [response, data] =
      await templateCategoryService.createTemplateCategory(newCategory);
    if (response.ok) {
      const [response, data] =
        await templateCategoryService.getTemplateCategories(lastFilter);
      if (response.ok) {
        setTemplateCategories(data);
      }
      open.change("create", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleUpdateTemplateCategory(newCategory) {
    const [response, data] =
      await templateCategoryService.updateTemplateCategory(newCategory.id, {
        ...newCategory,
        options: newCategory.options
          ? newCategory.options.map((option) => {
              return {
                name: option.label,
                sequence: option.sequence,
                value: option.id,
              };
            })
          : null,
      });
    if (response.ok) {
      const [response, data] =
        await templateCategoryService.getTemplateCategories(lastFilter);
      if (response.ok) {
        setTemplateCategories(data);
      }
      open.change("update", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  async function handleDeleteTemplateCategory(id) {
    const [response, data] =
      await templateCategoryService.removeTemplateCategory(id);
    if (response.ok) {
      const [response, data] =
        await templateCategoryService.getTemplateCategories(lastFilter);
      if (response.ok) {
        setTemplateCategories(data);
      }
      open.change("alert", false);
      return { success: true };
    } else {
      return { error: data.error };
    }
  }

  const rows = useMemo(() => {
    if (!templateCategories?.length)
      return templateCategories?.length === 0
        ? [
            {
              name: label("no-template-categories-were-found"),
            },
          ]
        : [
            {
              name: "Loading...",
            },
          ];
    return templateCategories.map((templateCategory) => {
      return {
        name: (
          <MDTypography variant="inherit">{templateCategory.name}</MDTypography>
        ),
        referenceCode: (
          <MDTypography variant="inherit">
            {templateCategory.referenceCode || label("none")}
          </MDTypography>
        ),
        template: (
          <MDTypography variant="inherit">
            {templateCategory.template?.name || "—"}
          </MDTypography>
        ),
        serialNumber: (
          <MDTypography variant="inherit">
            {templateCategory.serialNumber || "—"}
          </MDTypography>
        ),
        account: (
          <MDTypography variant="inherit">
            {templateCategory.account?.name || label("all")}
          </MDTypography>
        ),

        actions:
          (!templateCategory.accountId && can("isAdmin")) ||
          templateCategory.accountId ? (
            <MDBox
              display="flex"
              flexDirection="row"
              justifyContent="end"
              alignItems="center"
            >
              {permissions.update && (
                <Tooltip title={label("Edit")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("update", {
                        templateCategory,
                      });
                    }}
                  >
                    <Icon>edit</Icon>
                  </IconButton>
                </Tooltip>
              )}
              {permissions.delete && (
                <Tooltip title={label("Delete")}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      open.change("alert", {
                        templateCategory,
                      });
                    }}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          ) : null,
      };
    });
  }, [templateCategories, permissions]);

  const columns = useMemo(() => {
    const columns = [
      {
        Header: label("name-singular"),
        accessor: "name",
      },
      {
        Header: label("account-singular"),
        accessor: "account",
        width: "100%",
      },
    ];

    if (permissions.update || permissions.delete) {
      columns.push({
        Header: "",
        accessor: "actions",
        align: "right",
      });
    }

    return columns;
  }, [permissions]);

  return (
    <>
      {open.get("alert") ? (
        <AlertDialog
          open={Math.random()}
          setOpen={(v) => {
            open.change("alert", v);
          }}
          title={label("delete-alert-title")}
          content={label("delete-alert-message")}
          onOk={async () => {
            await handleDeleteTemplateCategory(
              open.get("alert").templateCategory.id,
            );
          }}
        />
      ) : null}
      {open.get("create") ? (
        <TemplateCategoriesDialog
          mode="create"
          isOpen={open.get("create") ? true : false}
          onSubmit={handleAddTemplateCategory}
          onClose={() => open.change("create", false)}
          data={null}
        />
      ) : null}
      {open.get("update") ? (
        <TemplateCategoriesDialog
          mode="edit"
          isOpen={open.get("update") ? true : false}
          onSubmit={handleUpdateTemplateCategory}
          onClose={() => open.change("update", false)}
          data={open.get("update").templateCategory}
        />
      ) : null}

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {label("template-category-plural")}{" "}
                  {permissions.get
                    ? `(${templateCategories?.length || 0})`
                    : ""}
                </MDTypography>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      open.change("create", true);
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-template-category-button")}
                  </MDButton>
                )}
              </MDBox>
              {permissions.get ? (
                <>
                  <MDBox p={2} mt={2}>
                    <TableSearch
                      id="all-templateCategories"
                      internalLoading={!Boolean(templateCategories)}
                      fullWidth
                      setData={setTemplateCategories}
                      onSearch={async (request, callback) => {
                        setLastFilter(request);
                        callback(
                          await templateCategoryService.getTemplateCategories(
                            request,
                          ),
                        );
                      }}
                      label={label("search-template-categories-label")}
                    />
                  </MDBox>
                  <MDBox>
                    <DataTable
                      loading={!Boolean(templateCategories)}
                      table={{ columns: columns, rows: rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  </MDBox>
                </>
              ) : (
                <AccessDenied type="component" />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}
